import React, { useState, useEffect } from "react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import apiService from "../../services/ApiService";
import {
  NetworkError,
  ApiError,
  type ApiScanRecord,
  type VerificatorMember,
} from "../../types/api";

interface DateRange {
  startDate: string;
  endDate: string;
}

export const ScanHistoryPanel: React.FC = () => {
  const [scanHistory, setScanHistory] = useState<ApiScanRecord[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: "",
    endDate: "",
  });
  const [selectedOperator, setSelectedOperator] = useState("");
  const [verificatorMembers, setVerificatorMembers] = useState<
    VerificatorMember[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Load verificator members on mount
    loadVerificatorMembers();
  }, []);

  useEffect(() => {
    // Load from API on mount and when filters change
    loadScanHistoryFromApi();
  }, [dateRange.startDate, dateRange.endDate, selectedOperator]);

  const loadVerificatorMembers = async () => {
    try {
      // Get verificator members from API
      const response = await apiService.getMembers({
        params: { role: "verificator" },
      });

      if (response) {
        const apiMembers = (response as unknown as VerificatorMember[]).map(
          (member) =>
            ({
              full_name: member.full_name,
              username: member.username,
              created_at: member.created_at,
            } as VerificatorMember)
        );

        setVerificatorMembers(apiMembers);
      }
    } catch (error) {
      console.error("Failed to load verificator members:", error);
      // Continue with empty list if API fails
      setVerificatorMembers([]);
    }
  };

  const refreshData = () => {
    if (apiService.isAuthenticated()) {
      loadScanHistoryFromApi();
      loadVerificatorMembers(); // Also refresh verificator list
    }
  };

  const loadScanHistoryFromApi = async () => {
    setIsLoading(true);
    setError("");

    try {
      if (!apiService.isAuthenticated()) {
        setError("Authentication required. Please log in again.");
        return;
      }

      // Fetch scan history from API with optional filters
      const filters: Record<string, string | number> = {
        limit: 1000, // Increased limit to get more comprehensive data
      };

      if (dateRange.startDate) {
        filters.start_date = dateRange.startDate;
      }

      if (dateRange.endDate) {
        filters.end_date = dateRange.endDate;
      }

      if (selectedOperator) {
        filters.operator_username = selectedOperator;
      }

      // Use cache bypass for refresh to get real-time data
      const response = await apiService.getScanHistory(filters, {
        bypassCache: true,
      });

      if (response.data) {
        // Sort by scanned_at descending (most recent first)
        const sortedScans = response.data.sort(
          (a, b) =>
            new Date(b.scanned_at).getTime() - new Date(a.scanned_at).getTime()
        );
        setScanHistory(sortedScans);
      }
    } catch (error) {
      if (error instanceof NetworkError) {
        setError("Network error. Please check your connection and try again.");
      } else if (error instanceof ApiError) {
        setError(`API Error: ${error.message}`);
      } else {
        setError("An unexpected error occurred");
        console.error("Load scan history error:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setDateRange({ startDate: "", endDate: "" });
    setSelectedOperator("");
  };

  return (
    <Card padding="md" className="overflow-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-800">
          Verification History
        </h2>

        {/* Status and controls */}
        <div className="flex items-center gap-3 text-sm">
          {isLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          )}

          <Button
            variant="secondary"
            size="sm"
            onClick={refreshData}
            disabled={isLoading || !apiService.isAuthenticated()}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="text-sm text-yellow-800">{error}</div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 space-y-3">
        {/* Date Range Picker */}
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date:
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) =>
                setDateRange((prev) => ({
                  ...prev,
                  startDate: e.target.value,
                }))
              }
              className="w-full p-2 border rounded"
              disabled={isLoading}
            />
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date:
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              min={dateRange.startDate}
              onChange={(e) =>
                setDateRange((prev) => ({
                  ...prev,
                  endDate: e.target.value,
                }))
              }
              className="w-full p-2 border rounded"
              disabled={isLoading}
            />
          </div>

          <Button
            variant="secondary"
            size="sm"
            onClick={clearFilters}
            disabled={isLoading}
            className="mb-0"
          >
            Clear
          </Button>
        </div>

        {/* Operator Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Verificator:
          </label>
          <select
            value={selectedOperator}
            onChange={(e) => setSelectedOperator(e.target.value)}
            className="w-full p-2 border rounded"
            disabled={isLoading}
          >
            <option value="">All Verificators</option>
            {verificatorMembers.map((member) => (
              <option key={member.username} value={member.username}>
                {member.full_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-gray-800">
            Scan Results ({scanHistory.length})
          </h3>
          {(dateRange.startDate || dateRange.endDate || selectedOperator) && (
            <div className="text-sm text-gray-600">Filtered results</div>
          )}
        </div>

        <div className="border rounded overflow-auto max-h-[500px]">
          {scanHistory.length > 0 ? (
            <ul className="divide-y">
              {scanHistory.map((scan, index) => (
                <li key={index} className="p-3 text-sm hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium font-mono">{scan.barcode}</div>
                    <div className="flex items-center gap-2 text-xs">
                      {scan.is_manual_override && (
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                          Manual
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      <span>
                        {new Date(scan.scanned_at).toLocaleString("en-US", {
                          timeZone: "America/Los_Angeles",
                        })}
                        {" by "}
                        {scan.operator_full_name}
                      </span>
                      <span>{scan.device_id}</span>
                      {scan.shift_id && (
                        <span className="text-gray-400">
                          Session: {scan.shift_name}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-6 text-center">
              <p className="text-gray-500">
                {isLoading
                  ? "Loading scans..."
                  : "No scans found for the selected criteria"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Help text */}
      {!dateRange.startDate &&
        !dateRange.endDate &&
        !selectedOperator &&
        apiService.isAuthenticated() &&
        !isLoading && (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              Use the filters above to search scan history, or view all recent
              scans
            </p>
          </div>
        )}
    </Card>
  );
};
